import BulkEditTransactionModal from '@/modules/cashFlow/BulkEditTransactionModal';
import EditTransactionModal from '@/modules/cashFlow/EditTransactionModal';
import useAccountDetailsFlyout from '@/hooks/useAccountDetailsFlyout';
import { useGetCashflowCategoriesQuery } from '@/store/cashFlowApi';
import {
  BankTransaction,
  useDeleteTransactionMutation,
  useUpdateTransactionMutation,
} from '@/store/transactionsApi';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoveToInvestmentModal from '@/modules/cashFlow/MoveToInvestmentModal';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Snackbar,
  Typography,
  Link,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { EmbeddableMenu } from '@/integrations/EmbeddableMenu';
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  type GridGroupNode,
  GridRenderCellParams,
  type GridListViewColDef,
  GridRowParams,
  GridRowSelectionModel,
  GridAggregationModel,
  gridRowNodeSelector,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import dayjs from '@/config/dayjs';
import { isTransactionInMonth } from '@/utils/cashFlowMonth';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocalization } from '@/hooks/useLocalization';
import { useManagedColumnVisibility } from '@/hooks/useManagedColumnVisibility';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';
import { useAutoResizeGroupingColumn } from '@/hooks/useAutoResizeGroupingColumn';
import { Capacitor } from '@capacitor/core';
import {
  APP_DATE_DISPLAY_FORMAT,
  formatAppDate,
  parseOptionalDate,
} from '@/utils/date';
import CustomToolbarForNative from '../CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { useModuleAccess } from '@/permissions';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import {
  GRID_COLUMN_WIDTH_MEDIUM,
  GRID_COLUMN_WIDTH_NARROW,
} from '@/constants/dimensions';
import { actionColumnConfig } from '../grids/gridColumnConfigs';
import {
  GridContainer,
  type GridContainerSize,
} from '@/components/grids/GridContainer';

//

interface CashFlowTableProps {
  transactions: BankTransaction[];
  selectedMonth?: string | null;
  selectedCategory?: string | null;
  bulkEditButtonContainer: HTMLDivElement | null;
  investorId?: number;
  size?: GridContainerSize;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// These BankTransaction fields are typed non-null, but the API can return
// null/undefined at runtime — handle that so `.trim()`/format never blow up or
// render "$NaN".
const trimSafe = (value: string | null | undefined): string =>
  (value ?? '').trim();

const formatAmount = (value: number | null | undefined): string =>
  value == null ? '' : currencyFormatter.format(value);

const CashFlowTable: React.FC<CashFlowTableProps> = ({
  transactions,
  selectedMonth,
  selectedCategory,
  bulkEditButtonContainer,
  investorId,
  size = 'md',
}) => {
  const apiRef = useGridApiRef();
  // EUD-440: clicking an account name opens the Account Details flyout.
  const { openAccountById, canOpenAccount, accountFlyout } =
    useAccountDetailsFlyout({ investorId });
  const { t } = useLocalization();
  const theme = useTheme();
  const { validateByCode } = useModuleAccess();
  const canEditManagedTransactions = validateByCode(
    'AllowManagedTransactionEditing',
  );
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedRowIds, setSelectedRowIds] = useState<GridRowSelectionModel>({
    ids: new Set<string | number>(),
    type: 'include',
  });
  const [isBulkEditModalOpen, setIsBulkEditModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const [updateTransaction, { isLoading: isSavingBulkEdit }] =
    useUpdateTransactionMutation();

  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  const [optimisticallyHiddenIds, setOptimisticallyHiddenIds] = useState<
    Set<string>
  >(new Set());

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] =
    useState<BankTransaction | null>(null);

  const [localTransactions, setLocalTransactions] = useState<BankTransaction[]>(
    [],
  );

  const isNative = Capacitor.getPlatform() !== 'web';

  useEffect(() => {
    setLocalTransactions(transactions);
  }, [transactions]);

  // Fetch categories for inline editing selects
  const { data: categoriesData } = useGetCashflowCategoriesQuery({
    InvestorId: investorId,
  });

  // Prepare category options for filters/grouping (read-only)
  const { subCategoryOptionsMap } = useMemo(() => {
    const subOptionsMap: { [key: string]: string[] } = {};

    if (categoriesData?.categories) {
      const parentMap: { [key: number]: string } = {};
      // First pass: get parent names and initialize map entries
      categoriesData.categories.forEach((cat) => {
        if (!cat.ParentId) {
          parentMap[cat.BankTransactionCategoryId] = cat.Name;
          subOptionsMap[cat.Name] = [];
        }
      });
      // Second pass: populate subcategories
      categoriesData.categories.forEach((cat) => {
        if (cat.ParentId && parentMap[cat.ParentId]) {
          const parentName = parentMap[cat.ParentId];
          subOptionsMap[parentName].push(cat.Name);
        }
      });
    }
    // Sort options alphabetically
    Object.values(subOptionsMap).forEach((arr) => arr.sort());

    return {
      subCategoryOptionsMap: subOptionsMap,
    };
  }, [categoriesData]);

  const { incomeParentName, incomeSubcategorySet } = useMemo(() => {
    const parentNames = Object.keys(subCategoryOptionsMap);
    const incomeParentName =
      parentNames.find((n) => n.toLowerCase() === 'income') || 'Income';
    const incomeSubs = new Set<string>(subCategoryOptionsMap[incomeParentName]);
    return { incomeParentName, incomeSubcategorySet: incomeSubs };
  }, [subCategoryOptionsMap]);

  // Generate rows from local state and filter optimistically hidden ones
  const rows = useMemo(() => {
    const filteredByMonth = selectedMonth
      ? localTransactions.filter((t) =>
          isTransactionInMonth(t.TransactionDate, selectedMonth),
        )
      : localTransactions;

    const filteredByCategory = selectedCategory
      ? filteredByMonth.filter(
          (t) =>
            t.ParentCategoryName === selectedCategory ||
            t.CategoryName === selectedCategory ||
            (selectedCategory === 'Uncategorized' &&
              !t.ParentCategoryName &&
              !t.CategoryName),
        )
      : filteredByMonth;

    return filteredByCategory
      .filter((t) => !optimisticallyHiddenIds.has(t.WealthAccountTransactionId))
      .map((transaction, index) => ({
        id: transaction.WealthAccountTransactionId || `temp-${String(index)}`,
        ...transaction,
        // Map to legacy Flow Type semantics: Income vs Spending
        CashflowGroup: (() => {
          const parentName = (
            transaction.ParentCategoryName || ''
          ).toLowerCase();
          const catName = transaction.CategoryName || '';
          if (parentName === (incomeParentName || '').toLowerCase())
            return 'Income';
          if (catName && incomeSubcategorySet.has(catName)) return 'Income';
          // Fallback: Treat all non-income as Spending (legacy behavior)
          return 'Spending';
        })(),
        GroupCategory:
          transaction.CategoryName ||
          transaction.ParentCategoryName ||
          t('uncategorized'),
      }));
  }, [
    localTransactions,
    optimisticallyHiddenIds,
    selectedMonth,
    selectedCategory,
    t,
    incomeParentName,
    incomeSubcategorySet,
  ]);

  const {
    selectedIds,
    filteredSelectedIds,
  }: { selectedIds: (string | number)[]; filteredSelectedIds: number[] } =
    useMemo(() => {
      const selectedIds =
        selectedRowIds.type === 'exclude'
          ? rows
              .map((r) => r.id as string | number)
              .filter((id) => !selectedRowIds.ids.has(id))
          : Array.from(selectedRowIds.ids as Set<string>);

      const filteredSelectedIds = selectedIds.filter(
        (id) => !isNaN(Number(id)),
      );
      return { selectedIds, filteredSelectedIds } as {
        selectedIds: (string | number)[];
        filteredSelectedIds: number[];
      };
    }, [selectedRowIds, rows]);

  const selectedCount = selectedIds.filter((id) => !isNaN(Number(id))).length;

  const groupingModel = useMemo(() => {
    if (selectedCategory) return ['GroupCategory'];
    if (selectedMonth) return ['CashflowGroup'];
    return ['ParentCategoryName'];
  }, [selectedCategory, selectedMonth]);

  // State for aggregation model - show sum of Amount on grouping rows
  const [aggregationModel, setAggregationModel] =
    useState<GridAggregationModel>({});

  // Update aggregation model when grouping changes
  useEffect(() => {
    if (groupingModel.length === 0) {
      setAggregationModel({});
    } else {
      setAggregationModel({ Amount: 'sum' });
    }
  }, [groupingModel]);

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    initialState: {
      rowGrouping: {
        model: groupingModel, // Use dynamic grouping model
      },
      pagination: {
        paginationModel: { pageSize: PAGE_SIZES.DEFAULT },
      },
    },
  });

  // Add state for the menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [activeTransaction, setActiveTransaction] =
    useState<BankTransaction | null>(null);

  // State for Edit Transaction modal
  const [editTransactionModalOpen, setEditTransactionModalOpen] =
    useState(false);
  const [editTransactionTx, setEditTransactionTx] =
    useState<BankTransaction | null>(null);

  const [moveToInvestmentModalOpen, setMoveToInvestmentModalOpen] =
    useState(false);
  const [moveToInvestmentTx, setMoveToInvestmentTx] =
    useState<BankTransaction | null>(null);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, transaction: BankTransaction) => {
      event.stopPropagation();
      setMenuAnchorEl(event.currentTarget);
      setActiveTransaction(transaction);
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setActiveTransaction(null);
  }, []);

  const handleMoveToInvestment = () => {
    if (!activeTransaction) return;
    setMoveToInvestmentTx(activeTransaction); // preserve before menu clears it
    setMoveToInvestmentModalOpen(true);
    handleMenuClose();
  };

  const handleEditTransaction = () => {
    if (!activeTransaction) return;
    setEditTransactionTx(activeTransaction);
    setEditTransactionModalOpen(true);
    handleMenuClose();
  };

  const canShowEditTransaction =
    !!activeTransaction &&
    ((activeTransaction.CanEdit as boolean | undefined) !== false ||
      canEditManagedTransactions);

  const baseColumns: GridColDef<BankTransaction>[] = useMemo(() => {
    return [
      {
        field: 'ParentCategoryName',
        headerName: t('grid.category'),
        width: GRID_COLUMN_WIDTH_MEDIUM,
        // Use a free-text filter input (valueOptions can be empty while loading,
        // which previously resulted in a blank filter form with no input).
        type: 'string',
      },
      {
        field: 'TransactionDate',
        headerName: t('date'),
        type: 'date',
        valueGetter: (value: string) => parseOptionalDate(value),
        renderCell: (params: GridRenderCellParams<BankTransaction, Date>) =>
          formatAppDate(params.value, {
            format: APP_DATE_DISPLAY_FORMAT,
            fallback: '',
          }),
        width: 120,
      },
      {
        field: 'DisplayDescription',
        headerName: t('description'),
        flex: 2,
        renderCell: (params: GridRenderCellParams<BankTransaction, string>) => {
          const logoUrl = params.row.MerchantLogoUrl;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                overflow: 'hidden',
              }}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt=''
                  width={20}
                  height={20}
                  loading='lazy'
                  style={{ borderRadius: 4, objectFit: 'contain' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : null}
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {params.value ?? ''}
              </span>
            </Box>
          );
        },
      },
      {
        field: 'CategoryName',
        headerName: t('grid.subcategory'),
        width: GRID_COLUMN_WIDTH_NARROW,
      },
      {
        field: 'Amount',
        headerName: t('grid.amount'),
        type: 'number',
        valueFormatter: (value: number | null | undefined) => {
          if (value === null || value === undefined) {
            return '';
          }
          return currencyFormatter.format(value);
        },
        aggregable: true,
        renderCell: (
          params: GridRenderCellParams<
            BankTransaction,
            number | null | undefined
          >,
        ) => {
          // Handle aggregation values
          if (params.rowNode.type === 'group') {
            const aggregatedValue = params.value;
            if (aggregatedValue !== null && aggregatedValue !== undefined) {
              return (
                <Box
                  sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    width: '100%',
                    textAlign: 'right',
                  }}
                >
                  {currencyFormatter.format(aggregatedValue)}
                </Box>
              );
            }
          }
          // Regular cell rendering
          if (params.value == null) return '';
          return currencyFormatter.format(params.value);
        },
        align: 'right',
        headerAlign: 'right',
        flex: 1,
      },
      {
        field: 'AccountNumber',
        headerName: t('grid.accountNumber'),
        flex: 1,
        renderCell: (params: GridRenderCellParams<BankTransaction>) => {
          const accountNumber = trimSafe(params.row.AccountNumber);
          const accountUrl = trimSafe(params.row.AccountUrl);

          if (!accountNumber) {
            return '';
          }

          if (isXs || !accountUrl) {
            return accountNumber;
          }

          return (
            <Link
              href={accountUrl}
              target='_blank'
              rel='noopener noreferrer'
              underline='hover'
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {accountNumber}
            </Link>
          );
        },
      },
      {
        field: 'Name',
        headerName: t('grid.accountName'),
        flex: 1,
        // EUD-440: clicking the account name opens the Account Details flyout.
        renderCell: (params: GridRenderCellParams<BankTransaction>) => {
          const name = params.row.Name;
          if (!name) return null;
          const accountId = params.row.WealthAccountId;
          if (!canOpenAccount(accountId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openAccountById(accountId);
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'TransactionType',
        headerName: t('grid.type'),
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        width: 80,
        cellClassName: 'actions',
        ...actionColumnConfig,
        getActions: ({ row }: { row: BankTransaction }) => {
          const isDisabled = deletingRowId === row.WealthAccountTransactionId;

          return [
            <GridActionsCellItem
              key={`actions-${row.WealthAccountTransactionId}`}
              icon={<MoreVertIcon />}
              label={t('actions', 'Actions')}
              onClick={(event: React.MouseEvent<HTMLElement>) => {
                handleMenuOpen(event, row);
              }}
              disabled={isDisabled}
              showInMenu={false}
              color='inherit'
            />,
          ];
        },
      },
    ];
  }, [canOpenAccount, deletingRowId, handleMenuOpen, isXs, openAccountById, t]);

  const {
    columnVisibilityModel,
    handleColumnVisibilityChange,
    applyColumnVisibility,
    defaultColumnVisibilityModel,
  } = useManagedColumnVisibility<BankTransaction>({
    hideDisabledColumnMenu: true,
    // Hide the currently-grouped column so its value isn't duplicated on leaf
    // rows (group header + a redundant "Category" column) — EUD-612.
    forcedHiddenFields: groupingModel,
  });

  const columns = useMemo(
    () => applyColumnVisibility(baseColumns),
    [baseColumns, applyColumnVisibility],
  );

  const listViewColDef: GridListViewColDef<BankTransaction> = useMemo(() => {
    return {
      field: 'listColumn',
      renderCell: (params: GridRenderCellParams<BankTransaction>) => {
        const { row, rowNode, api } = params;
        // The columnVisibilityModel is typed as Record<string, boolean> by MUI,
        // but fields absent from the model are visible at runtime. Keep the
        // honest `boolean | undefined` type so the `!== false` guard (undefined
        // => visible) below is preserved rather than flagged as redundant.
        const visibilityModel = api.state.columns
          .columnVisibilityModel as Record<string, boolean | undefined>;
        const isFieldVisible = (field: string) =>
          visibilityModel[field] !== false;

        // Group header rows
        if (rowNode.type === 'group') {
          const groupKey = String(rowNode.groupingKey ?? params.value ?? '');

          const isExpanded = !!rowNode.childrenExpanded;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            apiRef.current?.setRowChildrenExpansion(params.id, !isExpanded);
          };

          const nodeFromApi = gridRowNodeSelector(apiRef, params.id);
          const apiGroupNode =
            nodeFromApi.type === 'group'
              ? (nodeFromApi as GridGroupNode & { descendantCount?: number })
              : undefined;
          const rowGroupNode = rowNode as GridGroupNode & {
            descendantCount?: number;
          };
          const childCount =
            apiGroupNode?.descendantCount ??
            apiGroupNode?.children.length ??
            rowGroupNode.descendantCount ??
            rowGroupNode.children.length;

          const sumAmount = (
            rowNode as GridGroupNode & {
              aggregation?: { Amount?: { sum?: number } };
            }
          ).aggregation?.Amount?.sum;

          return (
            <Box
              sx={{
                width: '100%',
                px: 0,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Button
                onClick={handleToggleExpand}
                size='small'
                sx={{ minWidth: 28, px: 0.5 }}
                aria-label={
                  isExpanded
                    ? t('grid.collapseGroup', 'Collapse group')
                    : t('grid.expandGroup', 'Expand group')
                }
              >
                {isExpanded ? (
                  <ExpandMoreIcon fontSize='small' />
                ) : (
                  <ChevronRightIcon fontSize='small' />
                )}
              </Button>

              <Typography
                variant='body2'
                sx={{
                  fontWeight: '600 !important',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {`${groupKey} (${String(childCount)})`}
              </Typography>

              {typeof sumAmount === 'number' ? (
                <Typography
                  variant='body2'
                  sx={{ ml: 'auto', fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {currencyFormatter.format(sumAmount)}
                </Typography>
              ) : null}
            </Box>
          );
        }

        const description = trimSafe(row.DisplayDescription);
        const amount = formatAmount(row.Amount);

        const subcategory = trimSafe(row.CategoryName);
        const dateValue = formatAppDate(row.TransactionDate, {
          format: APP_DATE_DISPLAY_FORMAT,
        });

        const accountName = trimSafe(row.Name);
        const accountNumber = trimSafe(row.AccountNumber);

        // Checkbox for bulk edit in list view
        const hasSelectedId = (id: string | number) => {
          const ids = selectedRowIds.ids as Set<string | number>;
          if (ids.has(id)) return true;
          const idStr = String(id);
          if (ids.has(idStr)) return true;
          const idNum = Number(idStr);
          if (!Number.isNaN(idNum) && ids.has(idNum)) return true;
          return false;
        };

        const rowId = params.id;
        const isSelected =
          selectedRowIds.type === 'exclude'
            ? !hasSelectedId(rowId)
            : hasSelectedId(rowId);

        // CanEdit is typed boolean but can be absent at runtime; absent => editable.
        const isRowEditable = (row.CanEdit as boolean | undefined) !== false;

        const handleToggleSelected = (event: React.MouseEvent) => {
          event.stopPropagation();
          if (!isRowEditable) return;
          const currentIds = selectedRowIds.ids as Set<string | number>;
          const nextIds = new Set<string | number>(currentIds);

          if (selectedRowIds.type === 'exclude') {
            if (hasSelectedId(rowId)) {
              nextIds.delete(rowId);
              nextIds.delete(String(rowId));
            } else {
              nextIds.add(rowId);
            }
            apiRef.current?.setRowSelectionModel({
              type: 'exclude',
              ids: nextIds,
            });
          } else {
            if (hasSelectedId(rowId)) {
              nextIds.delete(rowId);
              nextIds.delete(String(rowId));
            } else {
              nextIds.add(rowId);
            }
            apiRef.current?.setRowSelectionModel({
              type: 'include',
              ids: nextIds,
            });
          }
        };

        const TwoColLine = (line: {
          left: string;
          right: string;
          leftSx?: object;
          rightSx?: object;
        }) => (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Typography
              variant='body2'
              sx={{ flex: 1, minWidth: 0, ...(line.leftSx ?? {}) }}
            >
              {line.left}
            </Typography>
            <Typography
              variant='body2'
              sx={{
                minWidth: 0,
                whiteSpace: 'nowrap',
                textAlign: 'right',
                ...(line.rightSx ?? {}),
              }}
            >
              {line.right}
            </Typography>
          </Box>
        );

        return (
          <Box
            sx={{
              width: '100%',
              px: 2,
              py: 2,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Checkbox
              size='small'
              checked={isSelected}
              onClick={handleToggleSelected}
              disabled={!isRowEditable}
              sx={{ p: 0.5, mt: 0.25 }}
              slotProps={{
                input: { 'aria-label': t('grid.selectRow', 'Select row') },
              }}
            />

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              {/* Line 1: Description + Amount (both bold) */}
              {(isFieldVisible('DisplayDescription') ||
                isFieldVisible('Amount')) && (
                <TwoColLine
                  left={
                    isFieldVisible('DisplayDescription')
                      ? description || t('common.na', 'N/A')
                      : t('grid.amount', 'Amount')
                  }
                  right={isFieldVisible('Amount') ? amount : ''}
                  leftSx={{
                    color: 'text.primary',
                    fontWeight: '600 !important',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  rightSx={{
                    color: 'text.primary',
                    fontWeight: '600 !important',
                  }}
                />
              )}

              {/* Line 2: Subcat + Date */}
              {(isFieldVisible('CategoryName') ||
                isFieldVisible('TransactionDate')) && (
                <TwoColLine
                  left={
                    isFieldVisible('CategoryName')
                      ? subcategory || t('common.na', 'N/A')
                      : t('date', 'Date')
                  }
                  right={isFieldVisible('TransactionDate') ? dateValue : ''}
                  leftSx={{ color: 'text.secondary' }}
                  rightSx={{ color: 'text.secondary' }}
                />
              )}

              {/* Line 3: Account Name */}
              {isFieldVisible('Name') && (
                <Typography
                  variant='body2'
                  onClick={
                    canOpenAccount(row.WealthAccountId)
                      ? () => {
                          openAccountById(row.WealthAccountId);
                        }
                      : undefined
                  }
                  sx={{
                    color: 'text.secondary',
                    minWidth: 0,
                    ...(canOpenAccount(row.WealthAccountId)
                      ? { cursor: 'pointer', textDecoration: 'underline' }
                      : {}),
                  }}
                >
                  {accountName || t('common.na', 'N/A')}
                </Typography>
              )}

              {/* Line 4: Acct # : Account Number */}
              {isFieldVisible('AccountNumber') && (
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', minWidth: 0 }}
                >
                  {t('grid.acctNumber', 'Acct #:')}{' '}
                  {accountNumber || t('common.na', 'N/A')}
                </Typography>
              )}

              <Button
                variant='text'
                size='small'
                startIcon={<MoreVertIcon fontSize='small' />}
                onClick={(event) => {
                  handleMenuOpen(event, row);
                }}
                disabled={deletingRowId === row.WealthAccountTransactionId}
                sx={{
                  alignSelf: 'flex-end',
                  minWidth: 0,
                  mt: 0.25,
                  px: 0,
                  py: 0.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  transition: theme.transitions.create([
                    'color',
                    'text-decoration-color',
                  ]),
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {t('actions', 'Actions')}
              </Button>
            </Box>
          </Box>
        );
      },
    };
  }, [
    apiRef,
    canOpenAccount,
    deletingRowId,
    handleMenuOpen,
    openAccountById,
    selectedRowIds,
    t,
    theme,
  ]);

  // Auto-resize first column when grouping changes and columns are loaded
  useAutoResizeGroupingColumn({
    apiRef,
    columnsForDisplay: columns,
    rowGroupingModel: groupingModel,
  });

  const handleSaveBulkEdit = async (updates: {
    description?: string;
    categoryId?: number;
    date?: string;
  }) => {
    if (selectedCount === 0) return;

    // Create an array of promises for each update call
    const updatePromises = selectedIds.map(async (id) => {
      // Find the original transaction data
      const originalTransaction = transactions.find(
        (t) => t.WealthAccountTransactionId === id,
      );

      if (!originalTransaction) {
        // Should ideally not happen if selection is based on current rows
        console.error(
          `Transaction with ID ${String(id)} not found in local data.`,
        );
        // Surface the failure so Promise.all rejects below.
        throw new Error(`Transaction ${String(id)} not found`);
      }

      // Create the full updated transaction object
      const updatedTransaction: BankTransaction = {
        ...originalTransaction,
      };

      // Apply updates from the modal
      if (updates.description !== undefined) {
        updatedTransaction.DisplayDescription = updates.description;
      }
      if (updates.categoryId !== undefined) {
        updatedTransaction.BankTransactionCategoryId = updates.categoryId;
        // Attempt to find the new category name from fetched data (optional but good for UI consistency)
        // Note: This requires categoriesData to be available or fetched separately
        // updatedTransaction.CategoryName = findCategoryName(updates.categoryId);
      }
      if (updates.date !== undefined) {
        // Format date to YYYY-MM-DD (remove time)
        updatedTransaction.TransactionDate = dayjs(updates.date).format(
          'YYYY-MM-DD',
        );
      }

      // Call the mutation with the full transaction object
      return updateTransaction({
        transactionId: id.toString(),
        transaction: updatedTransaction,
      }).unwrap();
    });

    try {
      // Wait for all update calls to complete
      await Promise.all(updatePromises);
      setSnackbar({
        open: true,
        message: 'Transactions updated successfully!',
        severity: 'success',
      });
      setIsBulkEditModalOpen(false);
      apiRef.current?.setRowSelectionModel({
        ids: new Set<string>(),
        type: 'include',
      }); // Clear selection
    } catch (err) {
      console.error('Failed to bulk update transactions (looping):', err);
      setSnackbar({
        open: true,
        message: 'One or more transactions failed to update.',
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(null);
  };

  const [deleteTransaction, { isLoading: isDeleting }] =
    useDeleteTransactionMutation();

  const handleDeleteClick = useCallback((transaction: BankTransaction) => {
    setTransactionToDelete(transaction);
    setConfirmationDialogOpen(true);
  }, []);

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
    setTransactionToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!transactionToDelete) return;

    const idToDelete = transactionToDelete.WealthAccountTransactionId;

    // Add row ID to loading state (optional, could use isDeleting directly)
    // setLoadingRowIds((prev) => new Set(prev).add(idToDelete));
    setDeletingRowId(idToDelete); // Set the specific deleting ID
    setConfirmationDialogOpen(false); // Close dialog immediately

    try {
      await deleteTransaction(idToDelete).unwrap();
      setSnackbar({
        open: true,
        message: t(
          'transactions.deleteSuccess',
          'Transaction deleted successfully!',
        ),
        severity: 'success',
      });
      setTransactionToDelete(null); // Clear after success
      // Optimistically hide the row
      setOptimisticallyHiddenIds((prev) => new Set(prev).add(idToDelete));
      // Row will disappear due to cache invalidation (and now optimistic update)
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      setSnackbar({
        open: true,
        message: t(
          'transactions.deleteFailed',
          'Failed to delete transaction.',
        ),
        severity: 'error',
      });
      setTransactionToDelete(null); // Clear even on error
    }
  };

  // Effect to cleanup optimistic hiding state when TRANSACTIONS PROP updates
  useEffect(() => {
    setOptimisticallyHiddenIds((prevHiddenIds) => {
      const currentTransactionIds = new Set(
        transactions.map((t) => t.WealthAccountTransactionId), // Still check against the PROP
      );
      // Keep only the optimistically-hidden ids that still exist in the prop.
      const nextHiddenIds = new Set<string>(
        [...prevHiddenIds].filter((id) => currentTransactionIds.has(id)),
      );
      // Preserve the same Set reference when nothing was removed, so the state
      // update is a no-op and does not trigger an extra render.
      const changed = nextHiddenIds.size !== prevHiddenIds.size;
      return changed ? nextHiddenIds : prevHiddenIds;
    });
  }, [transactions]); // This effect should still depend on the PROP

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Add Bulk Edit Button */}
      {bulkEditButtonContainer &&
        createPortal(
          <Button
            variant='contained'
            onClick={() => {
              setIsBulkEditModalOpen(true);
            }}
            disabled={selectedCount === 0 || isSavingBulkEdit}
          >
            {t('bulkEdit')} ({selectedCount})
          </Button>,
          bulkEditButtonContainer,
        )}

      {/* Data Grid */}
      <Box sx={{ flexGrow: 1 }}>
        <GridContainer
          isWidget={false}
          size={size}
        >
          <DataGridPremium
            // On XS list view, switching content can leave the grid with stale internal width
            // calculations until a resize occurs. Forcing a remount on XS fixes this.
            key={`${selectedMonth ? 'grouped' : 'ungrouped'}-${isXs ? 'xs' : 'grid'}`}
            rows={rows}
            columns={columns}
            apiRef={apiRef}
            checkboxSelection
            initialState={initialState}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            defaultGroupingExpansionDepth={0}
            aggregationModel={aggregationModel}
            onAggregationModelChange={setAggregationModel}
            getAggregationPosition={(groupNode: GridGroupNode | null) => {
              // MUI also calls this for the root group (the grid-level aggregate,
              // passed as null here) in addition to each row group. Return null for
              // the root so a grouped Cash Flow view doesn't add an all-rows
              // aggregate on top of the per-group sums (EUD-440 review).
              return groupNode == null ? null : 'inline';
            }}
            aggregationRowsScope='all'
            disableRowSelectionOnClick
            pagination
            pageSizeOptions={[...PAGE_SIZE_OPTIONS.STANDARD]}
            groupingColDef={{
              hideDescendantCount: false,
              width: undefined,
              flex: 1,
              minWidth: 200,
              // Prevent the generated grouping column from being offered as a
              // filterable "Category" option (it can create a duplicate entry and
              // a filter row with no usable value input).
              filterable: false,
            }}
            onRowGroupingModelChange={(newModel) => {
              // Update aggregation when grouping changes
              if (newModel.length === 0) {
                setAggregationModel({});
              } else {
                setAggregationModel({ Amount: 'sum' });
              }
            }}
            getRowClassName={(params: GridRowParams<BankTransaction>) => {
              if (deletingRowId === params.id) return 'row--deleting';
              return '';
            }}
            rowSelectionPropagation={{
              parents: false,
              descendants: true,
            }}
            isRowSelectable={(params) =>
              (params.row.CanEdit as boolean | undefined) !== false
            }
            onRowSelectionModelChange={(newSelectionModel) => {
              // console.log("newSelectionModel", newSelectionModel);
              setSelectedRowIds(newSelectionModel);
            }}
            rowSelectionModel={selectedRowIds}
            rowSelection
            // keepNonExistentRowsSelected // Important for pagination/filtering
            showToolbar
            disablePivoting
            listView={isXs}
            {...(isXs ? { listViewColumn: listViewColDef } : {})}
            {...(isXs ? { getRowHeight: () => 'auto' as const } : {})}
            slotProps={{
              toolbar: {
                ...defaultToolbarExportSlotProps,
                showQuickFilter: true,
              },
              columnsManagement: {
                defaultColumnVisibilityModel,
                ...(isXs
                  ? {
                      getTogglableColumns: (cols: GridColDef[]) =>
                        cols
                          .filter((c) => c.hideable !== false)
                          .map((c) => c.field)
                          .filter(
                            (field) =>
                              field !== 'actions' && // Actions
                              field !== 'CashflowGroup' && // Flow Type
                              field !== 'TransactionType' && // Type
                              field !== 'listColumn' &&
                              field !== '__list__' &&
                              field !== '__row_group_by_columns_group__' &&
                              !field.startsWith('__'),
                          ),
                    }
                  : {}),
              },
            }}
            slots={{
              columnsManagement: ManagedColumnsManagement,
              ...(isNative
                ? {
                    toolbar: (
                      props: React.ComponentProps<
                        typeof CustomToolbarForNative
                      >,
                    ) => <CustomToolbarForNative {...props} />,
                  }
                : isXs
                  ? { toolbar: XsPreferencesDrawerToolbar }
                  : {}),
            }}
            // Optional: Adjust row height if needed
            // getRowHeight={() => 'auto'}
            sx={{
              // Component-specific styles for deleting rows
              '& .row--deleting': {
                backgroundColor: (theme) => theme.palette.error.light + '80', // Semi-transparent red
                opacity: 0.7,
                pointerEvents: 'none', // Disable clicks on row content
              },
              '& .row--deleting .MuiDataGrid-cellCheckbox, & .row--deleting .MuiDataGrid-actionsCell':
                {
                  pointerEvents: 'auto', // Re-enable clicks specifically for checkbox/actions if needed
                },
              '& .MuiDataGrid-main :not(.MuiDataGrid-groupingCriteriaCellToggle) .MuiButtonBase-root':
                {
                  border: 'none',
                  boxShadow: 'none',
                },
              // Hide the aggregation function name (e.g., "sum") from column headers
              "& .MuiDataGrid-columnHeader[data-field='Amount'] .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-columnHeaderTitleContainerContent > span:last-child":
                {
                  display: 'none',
                },
              // Alternative selector for aggregation label
              "& .MuiDataGrid-columnHeader[data-field='Amount'] .MuiDataGrid-aggregationColumnHeaderLabel":
                {
                  display: 'none',
                },
              // Make Amount column header bold
              '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
              key: selectedMonth ? 'grouped' : 'ungrouped',
              ...(isXs && {
                // In list view, force the single list column to actually fill the grid width.
                // IMPORTANT: don't stretch the checkbox/actions cells (Bulk Edit must remain usable).
                '& .MuiDataGrid-row:not(.MuiDataGrid-row--group) .MuiDataGrid-cellEmpty':
                  {
                    display: 'none !important',
                  },
                '& .MuiDataGrid-main': { width: '100%' },
                '& .MuiDataGrid-virtualScroller': { width: '100%' },
                '& .MuiDataGrid-virtualScrollerRenderZone': {
                  width: '100% !important',
                  minWidth: '100% !important',
                  maxWidth: '100% !important',
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  width: '100% !important',
                },
                '& .MuiDataGrid-virtualScrollerContent > div': {
                  width: '100% !important',
                  maxWidth: '100% !important',
                },
                '& .MuiDataGrid-row': {
                  display: 'flex',
                  flexWrap: 'nowrap',
                  width: '100% !important',
                  minWidth: '100% !important',
                  maxWidth: '100% !important',
                },
                '& .MuiDataGrid-cellCheckbox': {
                  flex: '0 0 auto',
                },
                '& .MuiDataGrid-actionsCell': {
                  flex: '0 0 auto',
                },
                '& .MuiDataGrid-cell:not(.MuiDataGrid-cellCheckbox):not(.MuiDataGrid-actionsCell)':
                  {
                    display: 'flex',
                    flexWrap: 'nowrap',
                    width: '100% !important',
                    minWidth: '100% !important',
                    maxWidth: '100% !important',
                    flex: '1 1 0',
                  },
                '& .MuiDataGrid-cellContent': {
                  width: '100%',
                  flex: 1,
                  minWidth: 0,
                },
              }),
            }}
          />
        </GridContainer>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmationDialog}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {t('areYouSure', 'Are you sure?')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            {t(
              'transactions.deleteConfirmation',
              'Are you sure you want to delete this transaction?',
            )}
            <br />
            <strong>{transactionToDelete?.DisplayDescription || ''}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            onClick={handleCloseConfirmationDialog}
            disabled={isDeleting}
          >
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            size='large'
            variant='contained'
            onClick={() => {
              void handleConfirmDelete();
            }}
            autoFocus
            disabled={isDeleting}
          >
            {t('ok', 'OK')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Modal */}
      <BulkEditTransactionModal
        open={isBulkEditModalOpen}
        onClose={() => {
          setIsBulkEditModalOpen(false);
        }}
        onSave={(updates) => {
          void handleSaveBulkEdit(updates);
        }}
        count={selectedCount}
        isSaving={isSavingBulkEdit}
        transactionIds={filteredSelectedIds}
        investorId={investorId}
      />

      {/* Add Actions Menu */}
      <EmbeddableMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {canShowEditTransaction && (
          <MenuItem onClick={handleEditTransaction}>
            <EditIcon
              fontSize='small'
              sx={{ mr: 1 }}
            />
            {t('transactions.editTransaction', 'Edit Transaction')}
          </MenuItem>
        )}

        <MenuItem
          onClick={handleMoveToInvestment}
          disabled={!activeTransaction}
        >
          <CompareArrowsIcon
            fontSize='small'
            sx={{ mr: 1 }}
          />
          {t(
            'transactions.moveToInvestmentActivity',
            'Move to Investment Activity',
          )}
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (activeTransaction) {
              handleDeleteClick(activeTransaction);
            }
            handleMenuClose();
          }}
          disabled={!activeTransaction}
        >
          <DeleteIcon
            fontSize='small'
            sx={{ mr: 1 }}
          />
          {t('delete', 'Delete')}
        </MenuItem>
      </EmbeddableMenu>

      {/* Move To Investment Modal (legacy-parity) */}
      <MoveToInvestmentModal
        open={moveToInvestmentModalOpen}
        onClose={() => {
          setMoveToInvestmentModalOpen(false);
          setMoveToInvestmentTx(null);
        }}
        transaction={moveToInvestmentTx}
        onDone={() => {
          if (moveToInvestmentTx) {
            setOptimisticallyHiddenIds((prev) =>
              new Set(prev).add(moveToInvestmentTx.WealthAccountTransactionId),
            );
          }
          setMoveToInvestmentTx(null);
        }}
      />

      {/* Edit Transaction Modal */}
      <EditTransactionModal
        open={editTransactionModalOpen}
        onClose={() => {
          setEditTransactionModalOpen(false);
          setEditTransactionTx(null);
        }}
        transaction={editTransactionTx}
        investorId={investorId}
        onUpdated={(updated) => {
          setLocalTransactions((currentLocalTxs) =>
            currentLocalTxs.map((tx) =>
              tx.WealthAccountTransactionId ===
              updated.WealthAccountTransactionId
                ? updated
                : tx,
            ),
          );
        }}
      />

      {accountFlyout}

      {/* Snackbar for feedback */}
      {snackbar && (
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default CashFlowTable;
